// The live helper-home route (bead hf-e6-commitments-coverage-udt.9).
//
// Subscription plumbing and composition. Every word and every layout decision belongs
// to `MyWeekSurfaces.tsx`; every answer a person can give belongs to the landed
// request and recheck routes, which are placed here rather than reimplemented.
//
// ── IT INVENTS NO QUERIES ──────────────────────────────────────────────────────
//
// Four sections, and all four are already answered by functions that shipped with
// earlier beads: `requests.myRequests`, `commitments.myRechecks`,
// `commitments.listMyCommitments`, and the one plan read the shell already holds. The
// bead's whole premise is that the helper's problem was NAVIGATION, not missing data
// — so the fix is a screen, not a backend.
//
// ── READING `myRechecks` HERE AS WELL AS INSIDE `RecheckRoute` IS DELIBERATE ────
//
// It looks like the duplicate read `RequestRoutes.tsx` warns about, and it is not.
// Convex serves one subscription per (query, args) pair to every component that asks
// for it, so both readers are looking at the same value in the same tick — they
// CANNOT disagree, which is the property that warning is actually about. What this
// copy is for is the one thing the screen has to know and the recheck list cannot
// tell it: which agreements are currently questions, so the "You've agreed to"
// section can leave them out and stop contradicting the section above it.
//
// ── AND IT DECIDES NO PERMISSIONS ──────────────────────────────────────────────
//
// `listMyCommitments` and `myRequests` are scoped to the caller by the boundary — the
// server resolved whose these are from the Membership, and there is no person id to
// pass or to get wrong. The availability summary is filtered to `viewerPersonId`,
// which the plan read also supplies. Nothing here reads a role (AUT-007).

import { Skeleton } from "@herefor/design-system";
import { MY_WEEK_MESSAGES as COPY } from "@herefor/product-language";
import { useQuery } from "convex/react";
import { useState } from "react";

import { circleSettingsQuery } from "../../convex/circleSettings";
import { commitmentQueries } from "../../convex/commitments";
import type { LivePlanResult } from "../../convex/plan";
import { useConnectivity } from "../../offline/offline";
import { RecheckRoute } from "../reconfirmation";
import { PendingAskRoute } from "../requests";
import { MyAvailabilityPanel, MyCommitmentsPanel, MyWeekLayout } from "./MyWeekSurfaces";
import type { MyWeekFocus } from "./MyWeekSurfaces";
import { toMyAvailabilitySummary, toMyCommitmentRows } from "./myWeekData";

export interface MyWeekRouteProps {
  readonly result: LivePlanResult;
  /** The section a notification deep link named, or null for the plain screen. */
  readonly focus?: MyWeekFocus;
}

export function MyWeekRoute({ result, focus = null }: MyWeekRouteProps) {
  const settings = useQuery(circleSettingsQuery, { circleId: result.circleId });
  const commitments = useQuery(commitmentQueries.listMine, { circleId: result.circleId });
  const rechecks = useQuery(commitmentQueries.myRechecks, { circleId: result.circleId });
  const { actionsPaused } = useConnectivity();
  // ONE "now" for the whole render, taken once (the same discipline
  // `AvailabilityRoute` uses for its own projection). Reading the clock during render
  // makes a component's output depend on when React happened to re-run it, and the
  // only thing it feeds here is the availability expiry projection — which must not
  // flicker between "ending soon" and not as unrelated queries settle.
  const [projectionNow] = useState(() => Date.now());

  // Nothing is painted until the server has said what this person agreed to and what
  // is being asked of them. An "all clear" rendered while the reads are in flight
  // would tell somebody their week is empty and then contradict itself a tick later —
  // and on this screen the first of those two sentences is the one they would act on.
  if (settings === undefined || commitments === undefined || rechecks === undefined) {
    return <Skeleton label={COPY.loadingLabel} lines={5} />;
  }

  const timeZone = settings === null ? result.timeZone : settings.timeZone;
  const renewalHorizonDays = settings === null ? 0 : settings.availabilityRenewalHorizonDays;

  const context = {
    timeZone,
    publicPlanRef: result.publicPlanRef,
    viewerPersonId: result.viewerPersonId,
    now: projectionNow,
    renewalHorizonDays,
  };

  const recheckSupportNeedIds = new Set(rechecks.map((recheck) => recheck.supportNeedId));
  const commitmentRows = toMyCommitmentRows(result, commitments, recheckSupportNeedIds, context);
  const availabilitySummary = toMyAvailabilitySummary(result, context);

  return (
    <MyWeekLayout
      asks={
        <PendingAskRoute
          actionsPaused={actionsPaused}
          circleId={result.circleId}
          publicPlanRef={result.publicPlanRef}
          timeZone={timeZone}
        />
      }
      availability={
        <MyAvailabilityPanel
          availabilityHref={`/c/${result.publicPlanRef}/availability`}
          summary={availabilitySummary}
        />
      }
      commitments={<MyCommitmentsPanel commitments={commitmentRows} />}
      focus={focus}
      rechecks={
        <RecheckRoute
          actionsPaused={actionsPaused}
          circleId={result.circleId}
          publicPlanRef={result.publicPlanRef}
          timeZone={timeZone}
        />
      }
    />
  );
}
